import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import PeriodPicker, { DEFAULT_PERIOD, periodParams } from "@/components/PeriodPicker";
import { Zap, Clock, AlertTriangle, PlugZap, Inbox, Phone, Trophy, Euro, ArrowRight, GraduationCap, TrendingUp, TrendingDown, Target, Percent } from "lucide-react";

export default function AnalyticsSpeed() {
  const [period, setPeriod] = useState(DEFAULT_PERIOD);

  const { data, isLoading } = useQuery({
    queryKey: ["analytics-speed", period.key || period.mode],
    queryFn: async () => (await api.get("/analytics/speed", { params: periodParams(period) })).data,
  });

  return (
    <div className="space-y-6" data-testid="analytics-speed-page">
      {/* Titolo semplice */}
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Velocità di risposta</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Quanto in fretta rispondiamo a un nuovo lead. <strong>Più veloce rispondi, più vendi.</strong> L'obiettivo perfetto è rispondere entro <strong>1 giorno</strong>; entro <strong>3 giorni</strong> è il minimo accettabile.
        </p>
      </div>

      {/* Filtro periodo */}
      <PeriodPicker value={period} onChange={setPeriod} testPrefix="speed-period" />

      {isLoading ? (
        <>
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-32 w-full" />
        </>
      ) : (
        <SpeedContent data={data} period={period} />
      )}
    </div>
  );
}

function SpeedContent({ data, period }) {
  const connected = data?.pipedrive_connected;
  const bands = data?.response_bands || [];
  const sumBy = (keys) => bands.filter((b) => keys.includes(b.key)).reduce((a, b) => a + (b.lead_count || 0), 0);
  // Scala per giorni: verde ≤1g (perfetto), giallo 2g, arancione scuro 3g (minimo), rosso oltre 3g
  const green = sumBy(["le_15", "m_16_30", "m_31_60", "h_1_3", "h_3_6", "h_6_12", "h_12_24"]);
  const yellow = sumBy(["d_1_2"]);
  const orange = sumBy(["d_2_3"]);
  const never = sumBy(["never"]);
  const red = sumBy(["d_3_5", "d_5_7", "over_7"]) + never;
  const total = green + yellow + orange + red || 1;
  const pct = (n) => Math.round((n / total) * 100);
  const greenPct = pct(green);

  // Il colore del verdetto segue il TEMPO TIPICO (mediana): ≤1g verde, ≤2g½ giallo, ≤3g arancione, oltre rosso
  const medSec = data?.medians?.response_median_seconds;
  const verdict = medSec == null ? "red"
    : medSec <= 86400 ? "green"
    : medSec <= 216000 ? "yellow"
    : medSec <= 259200 ? "orange"
    : "red";
  const VERDICT = {
    green: { bg: "bg-emerald-50 border-emerald-200 text-emerald-800", dot: "bg-emerald-500", title: "Ottimo! Rispondiamo entro 1 giorno 👍", },
    yellow: { bg: "bg-yellow-50 border-yellow-200 text-yellow-900", dot: "bg-yellow-400", title: "Bene: di solito rispondiamo entro 2 giorni", },
    orange: { bg: "bg-orange-50 border-orange-200 text-orange-900", dot: "bg-orange-600", title: "Al limite: rispondiamo entro 3 giorni, puntiamo a meno", },
    red: { bg: "bg-rose-50 border-rose-200 text-rose-800", dot: "bg-rose-500", title: "Attenzione: rispondiamo oltre i 3 giorni", },
  }[verdict];

  const median = data?.medians?.response_median || "—";
  const saleMedian = data?.medians?.sale_median || "—";
  const s = data?.summary || {};
  const realizzoPct = s.matched ? Math.round((s.sales / s.matched) * 100) : 0;
  const periodLabel = period.label;

  return (
    <>
      {!connected && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="pipedrive-pending-banner">
          <PlugZap className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <p>Collega Pipedrive per vedere anche i dati sulle vendite. La velocità di risposta qui sotto usa già le date reali di arrivo dei lead.</p>
        </div>
      )}

      {/* HERO: verdetto + 2 numeri chiave */}
      <Card className={`border-2 shadow-sm ${VERDICT.bg}`} data-testid="speed-verdict">
        <CardContent className="p-6">
          <div className="flex items-center gap-2">
            <span className={`h-3 w-3 rounded-full ${VERDICT.dot}`} />
            <p className="text-base font-bold">{VERDICT.title}</p>
          </div>
          <div className="mt-5 grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-70">Tempo tipico di risposta</p>
              <p className="mt-1 font-display text-5xl font-bold tracking-tight" data-testid="typical-response-time">{median}</p>
              <p className="mt-1 text-sm opacity-80">Di solito rispondiamo dopo questo tempo (obiettivo: entro 1 giorno)</p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider opacity-70">Risposte entro 1 giorno</p>
              <p className="mt-1 font-display text-5xl font-bold tracking-tight" data-testid="fast-response-pct">{greenPct}%</p>
              <p className="mt-1 text-sm opacity-80">Su 100 lead, {greenPct} ricevono risposta entro un giorno</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SEMAFORO: barra + 3 cartellini */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <p className="mb-4 text-sm font-semibold">Come rispondiamo ai lead <span className="font-normal text-muted-foreground">({periodLabel})</span></p>
          {/* Barra a semaforo */}
          <div className="flex h-6 w-full overflow-hidden rounded-full" data-testid="speed-bar">
            <div className="bg-emerald-500" style={{ width: `${pct(green)}%` }} title={`Entro 1 giorno: ${green}`} />
            <div className="bg-yellow-400" style={{ width: `${pct(yellow)}%` }} title={`2 giorni: ${yellow}`} />
            <div className="bg-orange-600" style={{ width: `${pct(orange)}%` }} title={`3 giorni: ${orange}`} />
            <div className="bg-rose-500" style={{ width: `${pct(red)}%` }} title={`Oltre 3 giorni: ${red}`} />
          </div>
          <div className="mt-5 grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Bucket icon={Zap} tone="green" title="Perfetti" subtitle="Entro 1 giorno" count={green} pct={pct(green)} />
            <Bucket icon={Clock} tone="yellow" title="Buoni" subtitle="Entro 2 giorni" count={yellow} pct={pct(yellow)} />
            <Bucket icon={Clock} tone="orange" title="Al limite" subtitle="Entro 3 giorni (minimo)" count={orange} pct={pct(orange)} />
            <Bucket icon={AlertTriangle} tone="red" title="Troppo lente" subtitle={`Oltre 3 giorni${never ? ` · di cui ${never} mai contattati` : ""}`} count={red} pct={pct(red)} />
          </div>
        </CardContent>
      </Card>

      {/* VENDITE: funnel semplice + percentuale di realizzo */}
      {connected && (
        <Card className="shadow-sm">
          <CardContent className="p-6">
            <p className="text-sm font-semibold">Dai lead alle vendite</p>
            <p className="mb-4 text-xs text-muted-foreground" data-testid="funnel-period">
              Periodo: <strong>{periodLabel}</strong> — le vendite contano per <strong>data di aggiudicazione</strong>, anche se il lead è arrivato prima
            </p>
            <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
              <FunnelStat icon={Inbox} label="Lead ricevuti" value={s.matched} />
              <Arrow />
              <FunnelStat icon={Phone} label="Contattati" value={s.contacted} />
              <Arrow />
              <FunnelStat icon={Trophy} label="Vendite" value={s.sales} tone="green" />
              <Arrow />
              <FunnelStat icon={Percent} label="Di realizzo" value={`${realizzoPct}%`} tone="green" testid="conversion-rate" />
              <Arrow />
              <FunnelStat icon={Euro} label="Incasso" value={s.revenue} tone="green" />
            </div>
            <p className="mt-4 text-sm text-muted-foreground">
              <strong>{s.matched ?? 0}</strong> lead arrivati e <strong>{s.sales ?? 0}</strong> vendite chiuse nel periodo: <strong>{realizzoPct}% di realizzo</strong>. Tempo tipico per chiudere una vendita: <strong>{saleMedian}</strong>.
            </p>
          </CardContent>
        </Card>
      )}

      <SpeedComparison period={period} />
    </>
  );
}

function SpeedComparison({ period }) {
  const { data } = useQuery({
    queryKey: ["speed-comparison", period.key || period.mode],
    queryFn: async () => (await api.get("/analytics/speed-comparison", { params: periodParams(period) })).data,
  });
  if (!data) return null;
  const comm = data.commercials || [];
  const courses = data.courses || [];
  const tone = (sec) => (sec == null ? "rose" : sec <= 86400 ? "emerald" : sec <= 172800 ? "yellow" : sec <= 259200 ? "orange" : "rose");
  const PILL = { emerald: "bg-emerald-100 text-emerald-700", yellow: "bg-yellow-100 text-yellow-800", orange: "bg-orange-200 text-orange-900", rose: "bg-rose-100 text-rose-700" };
  const fastest = courses.slice(0, 5);
  const slowest = [...courses].reverse().slice(0, 5);
  const opportunities = data.opportunities || [];
  const byRealizzo = [...comm].sort((a, b) => (b.realizzo || 0) - (a.realizzo || 0));
  const maxRealizzo = byRealizzo.length ? Math.max(...byRealizzo.map((c) => c.realizzo || 0), 1) : 1;

  return (
    <div className="space-y-6">
      {/* Classifica commerciali per velocità */}
      <Card className="shadow-sm" data-testid="commercials-comparison">
        <CardContent className="p-6">
          <p className="mb-1 text-sm font-semibold">Chi risponde più in fretta ai lead</p>
          <p className="mb-4 text-xs text-muted-foreground">Dal più veloce al più lento (tempo tipico dall'arrivo del lead alla prima risposta)</p>
          {comm.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Ancora nessun dato</p>
          ) : (
            <div className="space-y-2">
              {comm.map((c, i) => (
                <div key={c.name} data-testid="commercial-row" className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 px-3 py-2">
                  <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-700"}`}>{i + 1}</span>
                  {i === 0 && <Trophy className="h-4 w-4 flex-shrink-0 text-emerald-600" />}
                  <span className="flex-1 truncate text-sm font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{c.count} lead</span>
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${PILL[tone(c.median_seconds)]}`}>{c.median_label}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Classifica commerciali per percentuale di realizzo (conversione) */}
      <Card className="border-2 border-emerald-200 shadow-sm" data-testid="realizzo-comparison">
        <CardContent className="p-6">
          <div className="mb-1 flex items-center gap-2">
            <Percent className="h-5 w-5 text-emerald-600" />
            <p className="text-sm font-bold text-emerald-900">Percentuale di realizzo — chi chiude di più</p>
          </div>
          <p className="mb-4 text-xs text-muted-foreground">Quanti lead diventano vendite (vendite ÷ lead assegnati). Dal migliore al peggiore.</p>
          {byRealizzo.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">Ancora nessun dato</p>
          ) : (
            <div className="space-y-2">
              {byRealizzo.map((c, i) => (
                <div key={c.name} data-testid="realizzo-row" className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 px-3 py-2">
                  <span className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold ${i === 0 ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-700"}`}>{i + 1}</span>
                  {i === 0 && <Trophy className="h-4 w-4 flex-shrink-0 text-emerald-600" />}
                  <span className="w-28 flex-shrink-0 truncate text-sm font-medium">{c.name}</span>
                  <div className="hidden h-2 flex-1 overflow-hidden rounded-full bg-slate-100 sm:block">
                    <div className="h-full rounded-full bg-emerald-500" style={{ width: `${((c.realizzo || 0) / maxRealizzo) * 100}%` }} />
                  </div>
                  <span className="text-xs text-muted-foreground">{c.sales}/{c.count} vendite</span>
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-sm font-bold text-emerald-700">{c.realizzo ?? 0}%</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Corsi da spingere: lenti ma con vendite */}
      {opportunities.length > 0 && (
        <Card className="border-2 border-violet-200 bg-violet-50 shadow-sm" data-testid="courses-to-push">
          <CardContent className="p-6">
            <div className="mb-1 flex items-center gap-2">
              <Target className="h-5 w-5 text-violet-600" />
              <p className="text-sm font-bold text-violet-900">Corsi da spingere 🚀</p>
            </div>
            <p className="mb-4 text-xs text-violet-800">
              Qui rispondiamo <strong>tardi</strong> ma questi corsi <strong>vendono comunque</strong>: rispondere più in fretta = più vendite. Intervieni prima su questi.
            </p>
            <div className="space-y-2">
              {opportunities.map((c) => (
                <div key={c.name} data-testid="opportunity-row" className="flex flex-wrap items-center gap-2 rounded-lg border border-violet-200 bg-white px-3 py-2 sm:flex-nowrap">
                  <GraduationCap className="h-4 w-4 flex-shrink-0 text-violet-500" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium" title={c.name}>{c.name}</span>
                  <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-700">{c.sales} vendite</span>
                  {c.revenue > 0 && <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-semibold text-emerald-700">{c.revenue_label}</span>}
                  <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-bold text-rose-700">risposta {c.median_label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Corsi avanti / indietro */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="shadow-sm" data-testid="courses-ahead">
          <CardContent className="p-6">
            <div className="mb-1 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-emerald-600" />
              <p className="text-sm font-semibold">Corsi seguiti meglio (più avanti)</p>
            </div>
            <p className="mb-4 text-xs text-muted-foreground">Le tipologie di corso a cui rispondiamo più in fretta</p>
            <CourseList items={fastest} tone="emerald" PILL={PILL} toneFn={tone} />
          </CardContent>
        </Card>
        <Card className="shadow-sm" data-testid="courses-behind">
          <CardContent className="p-6">
            <div className="mb-1 flex items-center gap-2">
              <TrendingDown className="h-5 w-5 text-rose-600" />
              <p className="text-sm font-semibold">Corsi lasciati indietro</p>
            </div>
            <p className="mb-4 text-xs text-muted-foreground">Le tipologie di corso a cui rispondiamo più lentamente</p>
            <CourseList items={slowest} tone="rose" PILL={PILL} toneFn={tone} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function CourseList({ items, PILL, toneFn }) {
  if (!items || items.length === 0) return <p className="py-6 text-center text-sm text-muted-foreground">Ancora nessun dato</p>;
  return (
    <div className="space-y-2">
      {items.map((c) => (
        <div key={c.name} data-testid="course-row" className="flex items-center gap-3 rounded-lg border border-border bg-secondary/30 px-3 py-2">
          <GraduationCap className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
          <span className="flex-1 truncate text-sm font-medium" title={c.name}>{c.name}</span>
          <span className="text-xs text-muted-foreground">{c.count}</span>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${PILL[toneFn(c.median_seconds)]}`}>{c.median_label}</span>
        </div>
      ))}
    </div>
  );
}

const TONES = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  yellow: "bg-yellow-50 text-yellow-800 border-yellow-200",
  orange: "bg-orange-100 text-orange-900 border-orange-300",
  red: "bg-rose-50 text-rose-700 border-rose-200",
};

function Bucket({ icon: Icon, tone, title, subtitle, count, pct }) {
  return (
    <div className={`rounded-xl border p-4 ${TONES[tone]}`} data-testid={`bucket-${tone}`}>
      <div className="flex items-center justify-between">
        <Icon className="h-5 w-5" />
        <span className="text-sm font-bold">{pct}%</span>
      </div>
      <p className="mt-3 font-display text-3xl font-bold tracking-tight">{count}</p>
      <p className="mt-1 text-sm font-semibold">{title}</p>
      <p className="text-xs opacity-80">{subtitle}</p>
    </div>
  );
}

function FunnelStat({ icon: Icon, label, value, tone, testid }) {
  const green = tone === "green";
  return (
    <div className={`flex-1 rounded-xl border p-4 text-center ${green ? "border-emerald-200 bg-emerald-50" : "border-border bg-secondary/40"}`} data-testid={testid}>
      <Icon className={`mx-auto h-5 w-5 ${green ? "text-emerald-600" : "text-muted-foreground"}`} />
      <p className={`mt-2 font-display text-2xl font-bold tracking-tight ${green ? "text-emerald-700" : ""}`}>{value ?? 0}</p>
      <p className="mt-0.5 text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    </div>
  );
}

function Arrow() {
  return <ArrowRight className="mx-auto hidden h-5 w-5 flex-shrink-0 text-muted-foreground sm:block" />;
}
